/**
 * Config-driven permissions model for Byte Pulse.
 *
 * This is the **single source of truth** for roles, capabilities, and access control.
 * Both the API (middleware) and frontend (UI gating) consume these definitions.
 *
 * To change who can do what:
 *   1. Edit ROLE_CAPABILITIES below.
 *   2. Optionally add new Capability values.
 *   3. No middleware or component code changes needed.
 */
// ---------------------------------------------------------------------------
// Capabilities — atomic permissions that guard specific actions
// ---------------------------------------------------------------------------
export const Capability = {
    // Form management
    FORM_CREATE: 'form:create',
    FORM_EDIT: 'form:edit',
    FORM_PUBLISH: 'form:publish',
    FORM_ARCHIVE: 'form:archive',
    FORM_DELETE: 'form:delete',
    FORM_LIST: 'form:list',
    FORM_VIEW: 'form:view',
    // Translations
    TRANSLATION_VIEW: 'translation:view',
    TRANSLATION_EDIT: 'translation:edit',
    // Submissions
    SUBMISSION_CREATE: 'submission:create',
    SUBMISSION_VIEW_OWN: 'submission:view_own',
    SUBMISSION_VIEW_ALL: 'submission:view_all',
    SUBMISSION_EXPORT: 'submission:export',
    // Links
    LINK_GENERATE: 'link:generate',
    LINK_VIEW: 'link:view',
    // Analytics
    ANALYTICS_VIEW: 'analytics:view',
    // Developer tools
    API_GUIDE_VIEW: 'api_guide:view',
    // API key management
    API_KEY_MANAGE: 'api_key:manage',
    // Auditor Feedback (Ops Triggers)
    AUDITOR_FEEDBACK_READ: 'auditor_feedback:read',
    AUDITOR_FEEDBACK_REPLAY: 'auditor_feedback:replay',
};
// ---------------------------------------------------------------------------
// Roles — logical groupings mapped from JWT claims
// ---------------------------------------------------------------------------
export const Role = {
    BRAND_ADMIN: 'brand_admin',
    AREA_MANAGER: 'area_manager',
    GENERAL_MANAGER: 'general_manager',
    SHIFT_LEAD: 'shift_lead',
};
// ---------------------------------------------------------------------------
// Role → Capability mapping (the config you edit to change permissions)
// ---------------------------------------------------------------------------
export const ROLE_CAPABILITIES = {
    [Role.BRAND_ADMIN]: [
        Capability.FORM_CREATE,
        Capability.FORM_EDIT,
        Capability.FORM_PUBLISH,
        Capability.FORM_ARCHIVE,
        Capability.FORM_DELETE,
        Capability.FORM_LIST,
        Capability.FORM_VIEW,
        Capability.TRANSLATION_VIEW,
        Capability.TRANSLATION_EDIT,
        Capability.SUBMISSION_CREATE,
        Capability.SUBMISSION_VIEW_OWN,
        Capability.SUBMISSION_VIEW_ALL,
        Capability.SUBMISSION_EXPORT,
        Capability.LINK_GENERATE,
        Capability.LINK_VIEW,
        Capability.ANALYTICS_VIEW,
        Capability.API_GUIDE_VIEW,
        Capability.API_KEY_MANAGE,
        Capability.AUDITOR_FEEDBACK_READ,
        Capability.AUDITOR_FEEDBACK_REPLAY,
    ],
    [Role.AREA_MANAGER]: [
        Capability.SUBMISSION_CREATE,
        Capability.SUBMISSION_VIEW_OWN,
        Capability.AUDITOR_FEEDBACK_READ,
    ],
    [Role.GENERAL_MANAGER]: [
        Capability.SUBMISSION_CREATE,
        Capability.SUBMISSION_VIEW_OWN,
    ],
    [Role.SHIFT_LEAD]: [
        Capability.SUBMISSION_CREATE,
    ],
};
export const ROLE_MAPPING_RULES = [
    // Brand-level admin roles (Byte Pulse form creators/managers)
    { pattern: 'Full Brand Access', role: Role.BRAND_ADMIN },
    { pattern: 'Full_Brand_Access', role: Role.BRAND_ADMIN },
    { pattern: 'FULL_BRAND_ACCESS', role: Role.BRAND_ADMIN },
    { pattern: 'PHDV Developer Role', role: Role.BRAND_ADMIN },
    { pattern: 'PHDV_Developer_Role', role: Role.BRAND_ADMIN },
    { pattern: 'PHDV_DEVELOPER_ROLE', role: Role.BRAND_ADMIN },
    { pattern: 'BRAND_ADMIN', role: Role.BRAND_ADMIN },
    { pattern: 'Brand Administrator', role: Role.BRAND_ADMIN },
    // Area-level roles
    { pattern: 'AREA_MANAGER', role: Role.AREA_MANAGER },
    { pattern: 'Area Manager', role: Role.AREA_MANAGER },
    { pattern: 'MULTI_STORE_USER', role: Role.AREA_MANAGER },
    // Store GM roles
    { pattern: 'STORE_GENERAL_MANAGER', role: Role.GENERAL_MANAGER },
    { pattern: 'General Manager', role: Role.GENERAL_MANAGER },
    // Shift lead / team member roles
    { pattern: 'STORE_SHIFT_LEAD', role: Role.SHIFT_LEAD },
    { pattern: 'Shift Lead', role: Role.SHIFT_LEAD },
    { pattern: 'STORE_USER', role: Role.SHIFT_LEAD },
];
// ---------------------------------------------------------------------------
// Resolver functions
// ---------------------------------------------------------------------------
/**
 * Extracts all JWT claim values that are checked for role resolution.
 * This is the canonical list of fields inspected from the decoded token's hutbot_user.
 */
export function extractRoleClaims(hutbotUser) {
    return [
        ...(hutbotUser.userRoleGroups ?? []),
        ...(hutbotUser.vendorTools ?? []),
        hutbotUser.userTypeGroup ?? '',
        hutbotUser.operationalRole ?? '',
    ].filter(Boolean);
}
/**
 * Resolves the highest-privilege Role from a set of JWT claim values.
 * Returns the first matching role based on ROLE_MAPPING_RULES order.
 * Returns undefined if no role matches.
 */
export function resolveRole(claims) {
    const claimsLower = claims.map(c => c.toLowerCase());
    for (const rule of ROLE_MAPPING_RULES) {
        const patternLower = rule.pattern.toLowerCase();
        if (claimsLower.some(claim => claim.includes(patternLower))) {
            return rule.role;
        }
    }
    return undefined;
}
/**
 * Resolves all Roles that match the given claims.
 * Useful when a user might have multiple roles (e.g. Brand Admin + Area Manager).
 * Returns roles in priority order (highest privilege first).
 */
export function resolveAllRoles(claims) {
    const claimsLower = claims.map(c => c.toLowerCase());
    const matched = new Set();
    for (const rule of ROLE_MAPPING_RULES) {
        const patternLower = rule.pattern.toLowerCase();
        if (claimsLower.some(claim => claim.includes(patternLower))) {
            matched.add(rule.role);
        }
    }
    // Return in priority order: brand_admin > area_manager > general_manager > shift_lead
    const priority = [Role.BRAND_ADMIN, Role.AREA_MANAGER, Role.GENERAL_MANAGER, Role.SHIFT_LEAD];
    return priority.filter(r => matched.has(r));
}
/**
 * Returns the union of capabilities for the given roles.
 */
export function getCapabilitiesForRoles(roles) {
    const caps = new Set();
    for (const role of roles) {
        const roleCaps = ROLE_CAPABILITIES[role];
        if (roleCaps) {
            for (const cap of roleCaps) {
                caps.add(cap);
            }
        }
    }
    return caps;
}
/**
 * End-to-end resolver: from JWT claims to a set of capabilities.
 * This is the primary function used by both the API middleware and the frontend.
 */
/**
 * Capabilities granted to every authenticated user, regardless of role.
 * These allow any logged-in user to submit forms and view their own submissions.
 */
export const BASELINE_CAPABILITIES = [
    Capability.SUBMISSION_CREATE,
    Capability.SUBMISSION_VIEW_OWN,
];
export function resolveCapabilities(claims) {
    const roles = resolveAllRoles(claims);
    const capabilities = getCapabilitiesForRoles(roles);
    // Every authenticated user gets baseline capabilities
    for (const cap of BASELINE_CAPABILITIES) {
        capabilities.add(cap);
    }
    return { roles, capabilities };
}
/**
 * Checks if a set of capabilities includes the required capability.
 */
export function hasCapability(capabilities, required) {
    return capabilities.has(required);
}
/**
 * Checks if a set of capabilities includes ALL of the required capabilities.
 */
export function hasAllCapabilities(capabilities, required) {
    return required.every(cap => capabilities.has(cap));
}
/**
 * Checks if a set of capabilities includes ANY of the required capabilities.
 */
export function hasAnyCapability(capabilities, required) {
    return required.some(cap => capabilities.has(cap));
}
